import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ThemeClasses } from "../../../types/components";
import type {
  AvailabilitySlot,
  MaintenanceAppointment,
  ServiceCatalogItem,
  VehicleOption,
} from "../types";
import { fetchAvailability } from "../services/maintenanceService";

// Reservation form for the workshop agenda. Picks a vehicle + service (which
// drives the duration), a day, and one of the *available* slots returned by the
// backend (slots already full on every bay are disabled), then submits a
// create / reschedule. The available-slot list is the front line against
// over-booking; the backend re-validates capacity on submit.
interface AppointmentModalProps {
  mode: "create" | "edit";
  initial?: MaintenanceAppointment | null;
  vehicles: VehicleOption[];
  services: ServiceCatalogItem[];
  defaultDurationMinutes: number;
  defaultDate: string; // YYYY-MM-DD
  themeClasses: ThemeClasses;
  onCancel: () => void;
  onSubmit: (values: Partial<MaintenanceAppointment>) => Promise<void>;
}

function localDateKey(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function timeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function AppointmentModal({
  mode,
  initial,
  vehicles,
  services,
  defaultDurationMinutes,
  defaultDate,
  themeClasses,
  onCancel,
  onSubmit,
}: AppointmentModalProps) {
  const { t } = useTranslation("common");

  const [vehicleId, setVehicleId] = useState(initial?.vehicleId ?? "");
  const [serviceId, setServiceId] = useState(initial?.serviceId ?? "");
  const [duration, setDuration] = useState<number>(
    initial?.durationMinutes || defaultDurationMinutes
  );
  const [date, setDate] = useState(
    localDateKey(initial?.start) || defaultDate
  );
  const [slotStart, setSlotStart] = useState(initial?.start ?? "");
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [contactEmail, setContactEmail] = useState(initial?.contactEmail ?? "");

  const [slots, setSlots] = useState<AvailabilitySlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const inputBase =
    "w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors bg-app border-line text-content focus:border-primary-500";

  // Recompute availability whenever the day or duration changes.
  useEffect(() => {
    if (!date || !duration) {
      setSlots([]);
      return;
    }
    let alive = true;
    setLoadingSlots(true);
    fetchAvailability({ date, durationMinutes: duration })
      .then((res) => alive && setSlots(res.slots ?? []))
      .catch(() => alive && setSlots([]))
      .finally(() => alive && setLoadingSlots(false));
    return () => {
      alive = false;
    };
  }, [date, duration]);

  // When editing, the appointment's own slot may report 0 remaining (it is the
  // booking that fills it); always keep it selectable so it isn't lost.
  const slotOptions = useMemo(() => {
    const list = [...slots];
    if (
      mode === "edit" &&
      initial?.start &&
      localDateKey(initial.start) === date &&
      !list.some((s) => s.start === initial.start)
    ) {
      list.push({
        start: initial.start,
        end: initial.end,
        available: true,
        remaining: 1,
      });
      list.sort(
        (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
      );
    }
    return list;
  }, [slots, mode, initial, date]);

  const handleServiceChange = (id: string) => {
    setServiceId(id);
    const svc = services.find((s) => s.id === id);
    if (svc?.durationMinutes) setDuration(svc.durationMinutes);
    setSlotStart(""); // duration changed → previous slot may no longer be valid
  };

  const handleSubmit = async () => {
    setError(null);
    if (!vehicleId) return setError(t("mantenimiento.requiredField"));
    if (!slotStart) return setError(t("mantenimiento.agenda.pickSlot"));
    const svc = services.find((s) => s.id === serviceId);
    setSubmitting(true);
    try {
      await onSubmit({
        vehicleId,
        serviceId: serviceId || null,
        serviceName: svc?.name ?? null,
        durationMinutes: Number(duration) || defaultDurationMinutes,
        start: slotStart,
        notes: notes || null,
        contactEmail: contactEmail || null,
      });
    } catch (e) {
      const msg =
        (e as { response?: { data?: { meta?: { message?: unknown } } } })
          ?.response?.data?.meta?.message;
      setError(
        typeof msg === "string" ? msg : t("mantenimiento.agenda.bookingError")
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className={`mb-1 block text-xs font-medium ${themeClasses.textSecondary}`}>
            {t("mantenimiento.fields.vehicle")}
            <span className="ml-0.5 text-red-500">*</span>
          </label>
          <select
            value={vehicleId}
            onChange={(e) => setVehicleId(e.target.value)}
            className={inputBase}
          >
            <option value="">{t("mantenimiento.selectVehicle")}</option>
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>
                {v.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={`mb-1 block text-xs font-medium ${themeClasses.textSecondary}`}>
            {t("mantenimiento.fields.serviceName")}
          </label>
          <select
            value={serviceId ?? ""}
            onChange={(e) => handleServiceChange(e.target.value)}
            className={inputBase}
          >
            <option value="">—</option>
            {services.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
                {s.durationMinutes ? ` (${s.durationMinutes} min)` : ""}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={`mb-1 block text-xs font-medium ${themeClasses.textSecondary}`}>
            {t("mantenimiento.fields.duration")} (min)
          </label>
          <input
            type="number"
            min={5}
            value={duration}
            onChange={(e) => {
              setDuration(Number(e.target.value) || 0);
              setSlotStart("");
            }}
            className={inputBase}
          />
        </div>

        <div>
          <label className={`mb-1 block text-xs font-medium ${themeClasses.textSecondary}`}>
            {t("mantenimiento.fields.date")}
            <span className="ml-0.5 text-red-500">*</span>
          </label>
          <input
            type="date"
            value={date}
            onChange={(e) => {
              setDate(e.target.value);
              setSlotStart("");
            }}
            className={inputBase}
          />
        </div>

        <div className="sm:col-span-2">
          <label className={`mb-1 block text-xs font-medium ${themeClasses.textSecondary}`}>
            {t("mantenimiento.agenda.availableSlots")}
            <span className="ml-0.5 text-red-500">*</span>
          </label>
          {loadingSlots ? (
            <div className={`text-sm ${themeClasses.textSecondary}`}>
              <i className="ri-loader-4-line animate-spin mr-1" />
              {t("mantenimiento.loading")}
            </div>
          ) : slotOptions.length === 0 ? (
            <p className={`text-sm ${themeClasses.textSecondary}`}>
              {t("mantenimiento.agenda.noSlots")}
            </p>
          ) : (
            <div className="grid max-h-44 grid-cols-2 gap-2 overflow-y-auto sm:grid-cols-3">
              {slotOptions.map((s) => {
                const selected = slotStart === s.start;
                const disabled = !s.available;
                return (
                  <button
                    type="button"
                    key={s.start}
                    disabled={disabled}
                    onClick={() => setSlotStart(s.start)}
                    className={`rounded-lg border px-2 py-1.5 text-xs transition-colors ${
                      selected
                        ? "border-primary-500 bg-primary-500 text-white"
                        : disabled
                        ? `cursor-not-allowed opacity-40 ${themeClasses.border}`
                        : `${themeClasses.border} ${themeClasses.text} hover:border-primary-500`
                    }`}
                    title={`${t("mantenimiento.agenda.bays")}: ${s.remaining}`}
                  >
                    {timeLabel(s.start)}
                    {!disabled && (
                      <span className="ml-1 opacity-70">· {s.remaining}</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div>
          <label className={`mb-1 block text-xs font-medium ${themeClasses.textSecondary}`}>
            {t("mantenimiento.agenda.contactEmail")}
          </label>
          <input
            type="email"
            value={contactEmail ?? ""}
            placeholder={t("mantenimiento.reportEmailPlaceholder")}
            onChange={(e) => setContactEmail(e.target.value)}
            className={inputBase}
          />
        </div>

        <div className="sm:col-span-2">
          <label className={`mb-1 block text-xs font-medium ${themeClasses.textSecondary}`}>
            {t("mantenimiento.fields.description")}
          </label>
          <textarea
            rows={2}
            value={notes ?? ""}
            onChange={(e) => setNotes(e.target.value)}
            className={inputBase}
          />
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-500">
          <i className="ri-error-warning-line mr-1" />
          {error}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <button
          onClick={onCancel}
          className={`rounded-lg border px-4 py-2 text-sm ${themeClasses.border} ${themeClasses.buttonText} ${themeClasses.buttonHover}`}
        >
          {t("mantenimiento.cancel")}
        </button>
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="rounded-lg bg-primary-500 px-4 py-2 text-sm text-on-primary transition-colors hover:bg-primary-400 disabled:opacity-60"
        >
          {submitting
            ? t("mantenimiento.loading")
            : mode === "edit"
            ? t("mantenimiento.agenda.reschedule")
            : t("mantenimiento.agenda.reserve")}
        </button>
      </div>
    </div>
  );
}
